/**
 * A2F Streamer Service
 * 
 * Streams Realtime API response audio to Unreal Engine via Pixel Streaming.
 * UE will forward the audio to A2F NIM via the ACE adapter.
 * 
 * Audio is passed through unchanged (24kHz PCM 16-bit) - A2F NIM handles
 * downsampling to 16kHz internally while preserving quality.
 * 
 * @see docs/A2F_NIM_INTEGRATION.md for architecture details
 */

import logger from '@/lib/logger';

interface Streamer {
  emitUIInteraction: (data: object) => boolean;
}

interface AudioChunkMessage {
  type: 'audioChunk';
  data: string;           // Base64-encoded PCM 16-bit audio
  sampleRate: number;     // Realtime API native rate (24000)
  channels: 1;            // Mono (required by A2F)
  format: 'pcm16';        // Audio format identifier
  sequence: number;       // Chunk sequence number for ordering
  sessionId: string;      // For tracking related chunks
}

interface AudioStreamEndMessage {
  type: 'audioStreamEnd';
  sessionId: string;      // Must match the audioChunk sessionId
  totalChunks: number;    // Total chunks sent (for validation)
}

interface AudioStreamCancelMessage {
  type: 'audioStreamCancel';
  sessionId: string;
}

export class A2FStreamer {
  private streamer: Streamer | null = null;
  private chunkSequence = 0;
  private currentSessionId = '';
  // Promise chain to ensure audio chunks are processed in order
  // and finalizeStream waits for all pending chunks
  private chunkChain: Promise<void> = Promise.resolve();

  /**
   * Set the Pixel Streaming instance to use for sending audio.
   * Should be called when WebRTC connection is established.
   */
  setStreamer(streamer: Streamer | null): void {
    this.streamer = streamer;
    if (streamer) {
      logger.info('🎬 A2FStreamer: Pixel Streaming connection established');
    } else {
      logger.info('🎬 A2FStreamer: Pixel Streaming connection cleared');
    }
  }

  /**
   * Check if the streamer is connected and ready to send audio.
   */
  isReady(): boolean {
    return this.streamer !== null;
  }

  /**
   * Send an audio chunk from Realtime API to Unreal Engine.
   * Audio is passed through unchanged - A2F NIM handles conversion.
   * 
   * Uses a promise chain to ensure chunks are processed in order and
   * finalizeStream can wait for all pending chunks to complete.
   * 
   * @param base64Chunk - Base64-encoded PCM 16-bit audio from Realtime API
   * @param sampleRate - Sample rate of the audio (typically 24000)
   */
  async sendRealtimeAudioChunk(base64Chunk: string, sampleRate: number): Promise<void> {
    // Chain this chunk operation to ensure ordering and allow finalizeStream to wait
    this.chunkChain = this.chunkChain.then(() => {
      return this.sendChunkInternal(base64Chunk, sampleRate);
    });
    
    // Return the chain so callers can await if needed
    return this.chunkChain;
  }
  
  /**
   * Internal method to actually send the audio chunk.
   * Called from the promise chain to ensure ordering.
   */
  private sendChunkInternal(base64Chunk: string, sampleRate: number): void {
    if (!this.streamer) {
      logger.debug('⚠️ A2FStreamer: Cannot send audio - Pixel Streaming not connected');
      return;
    }

    // Generate session ID on first chunk of a new response
    if (this.chunkSequence === 0) {
      this.currentSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      logger.info(`🎬 A2FStreamer: Starting audio stream — session ${this.currentSessionId}`);
    }

    // No resampling needed - pass through 24kHz audio directly
    // A2F NIM handles downsampling reliably while preserving quality
    const message: AudioChunkMessage = {
      type: 'audioChunk',
      data: base64Chunk,
      sampleRate: sampleRate,
      channels: 1,
      format: 'pcm16',
      sequence: this.chunkSequence,
      sessionId: this.currentSessionId,
    };

    try {
      const sent = this.streamer.emitUIInteraction(message);
      this.chunkSequence++;

      // emitUIInteraction returns false when the Pixel Streaming data channel
      // isn't open yet (e.g. audio arrives before the channel finishes opening
      // on a fresh connection). Such chunks are silently dropped by the SDK, so
      // surface it — this is the signature of "video idles but no lip-sync".
      if (sent === false && this.chunkSequence === 1) {
        logger.warn('⚠️ A2FStreamer: data channel not open — audio chunk dropped (no lip-sync expected this turn)');
      }
      logger.debug(`🎬 A2FStreamer: chunk #${this.chunkSequence} (${base64Chunk.length} chars, ${sampleRate}Hz)`);
    } catch (error) {
      console.error('❌ A2FStreamer: Failed to send audio chunk:', error);
    }
  }

  /**
   * Signal the end of the current audio stream.
   * Called when Realtime API sends response.audio.done event.
   * 
   * Waits for all pending audio chunks to be sent before sending the end message.
   * This prevents race conditions where finalizeStream runs before all chunks are sent.
   */
  async finalizeStream(): Promise<void> {
    // Wait for all pending audio chunks to complete
    await this.chunkChain;
    
    if (!this.streamer) {
      logger.debug('⚠️ A2FStreamer: Cannot finalize - Pixel Streaming not connected');
      return;
    }

    if (this.chunkSequence === 0) {
      // No chunks were sent, nothing to finalize
      return;
    }

    const message: AudioStreamEndMessage = {
      type: 'audioStreamEnd',
      sessionId: this.currentSessionId,
      totalChunks: this.chunkSequence,
    };

    try {
      this.streamer.emitUIInteraction(message);
      logger.info(`🎬 A2FStreamer: Stream finalized — session ${this.currentSessionId}, ${this.chunkSequence} chunks`);
    } catch (error) {
      console.error('❌ A2FStreamer: Failed to send stream end message:', error);
    }

    // Reset for next response
    this.chunkSequence = 0;
    this.currentSessionId = '';
  }

  /**
   * Reset the streamer state without sending end message.
   * Useful for error recovery or forced cleanup.
   */
  reset(): void {
    this.chunkSequence = 0;
    this.currentSessionId = '';
    this.chunkChain = Promise.resolve(); // Reset the chain
    logger.debug('🎬 A2FStreamer: State reset');
  }

  cancelCurrentStream(): void {
    if (this.streamer && this.currentSessionId) {
      const message: AudioStreamCancelMessage = {
        type: 'audioStreamCancel',
        sessionId: this.currentSessionId
      };
      try {
        this.streamer.emitUIInteraction(message);
        console.log(`🎬 A2FStreamer: ⛔ Stream cancel sent - session ${this.currentSessionId}`);
      } catch (error) {
        console.error('❌ A2FStreamer: Failed to send stream cancel message:', error);
      }
    }
    this.reset();
  }
}

export const a2fStreamer = new A2FStreamer();
